import { authClient } from '@/config/auth-client';
import { ENV } from '@/config/env';
import { createLogger } from '@/utils/logger';
import Sentry from '@/utils/sentry';
import { handleDemoRestricted } from './demo-restriction';
import { sendMultipart } from './upload-transport';

// API Configuration - Get URL from centralized env config
const API_BASE_URL = ENV.API_URL;
const AUTH_BASE_URL = ENV.AUTH_URL;

// Create scoped logger for API client (dev-only logging)
const logger = createLogger('API');


// API Error Types
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

// Request Configuration
interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
  requireAuth?: boolean;
  signal?: AbortSignal;
  timeout?: number; // Timeout in milliseconds
  _retry401?: boolean;
}

// Authenticated API Client
export class ApiClient {
  private baseUrl: string;
  private jwtToken: string | null = null;
  private jwtExpiresAt: Date | null = null;
  /**
   * The refresh in flight, shared by every caller that finds the cache empty.
   * Without it, the burst of parallel requests at app start (or on expiry) each
   * starts its own refresh, hitting the auth service with a dozen or more
   * get-session calls at once.
   */
  private jwtRefresh: Promise<string | null> | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_BASE_URL;
  }

  private logger = createLogger('API');

  /**
   * Get the session token (for Better-Auth server authentication)
   */
  private async getSessionToken(): Promise<string | null> {
    try {
      logger.log('Getting session token for Better-Auth...');
      const sessionResponse = await authClient.getSession();

      logger.log('Session response structure:', {
        hasData: !!sessionResponse?.data,
        hasUser: !!sessionResponse?.data?.user,
        hasSession: !!sessionResponse?.data?.session,
        sessionKeys: sessionResponse?.data ? Object.keys(sessionResponse.data) : [],
      });

      // Extract session token (used for Better-Auth server communication)
      const sessionToken = sessionResponse?.data?.session?.token || null;

      if (sessionToken) {
        logger.log('Session token found');
      } else {
        logger.warn('No session token found in session response');
      }

      return sessionToken;
    } catch (error) {
      logger.error('Failed to get session token:', error);
      return null;
    }
  }

  /**
   * Get JWT token for data API authentication (with caching). Concurrent
   * callers share a single in-flight refresh.
   */
  private getJWTToken(): Promise<string | null> {
    // Check if current JWT is still valid (with 1 minute buffer)
    if (this.jwtToken && this.jwtExpiresAt && new Date() < this.jwtExpiresAt) {
      logger.log('Using cached JWT token');
      return Promise.resolve(this.jwtToken);
    }

    if (!this.jwtRefresh) {
      const refresh = this.fetchJWTToken().finally(() => {
        // Only clear our own promise: clearTokenCache may have dropped it and a
        // newer refresh may already be in flight.
        if (this.jwtRefresh === refresh) {
          this.jwtRefresh = null;
        }
      });
      this.jwtRefresh = refresh;
    }
    return this.jwtRefresh;
  }

  /**
   * Fetch a fresh JWT from the auth service and cache it.
   */
  private async fetchJWTToken(): Promise<string | null> {
    try {
      logger.log('Fetching fresh JWT token...');

      // Method 1: Try to get JWT from header first
      let jwtFromHeader: string | null = null;
      await authClient.getSession({
        fetchOptions: {
          onSuccess: (ctx) => {
            jwtFromHeader = ctx.response.headers.get("set-auth-jwt");
            if (jwtFromHeader) {
              logger.log('JWT found in header');
            }
          }
        }
      });

      if (jwtFromHeader) {
        // Cache the JWT token (15 min expiry with 1 min buffer = 14 min cache)
        this.jwtToken = jwtFromHeader;
        this.jwtExpiresAt = new Date(Date.now() + 14 * 60 * 1000);
        return jwtFromHeader;
      }

      // Method 2: Use /token endpoint if header method fails
      logger.log('No JWT in header, trying /token endpoint...');
      const sessionToken = await this.getSessionToken();

      if (!sessionToken) {
        logger.log('No session token available for JWT request');
        return null;
      }

      const response = await fetch(`${AUTH_BASE_URL}/api/auth/token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        logger.error('JWT token request failed:', response.status, response.statusText);
        return null;
      }

      const tokenData = await response.json();
      const jwtToken = tokenData.token;

      if (jwtToken) {
        logger.log('JWT token retrieved from endpoint');
        // Cache the JWT token
        this.jwtToken = jwtToken;
        this.jwtExpiresAt = new Date(Date.now() + 14 * 60 * 1000); // 14 minutes cache
        return jwtToken;
      }

      logger.warn('No JWT token found in response');
      return null;

    } catch (error) {
      logger.error('Failed to get JWT token:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      // If it's a network error, throw NetworkError instead of returning null
      // This allows network errors to propagate correctly instead of being converted to ApiError(401)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkError('Network request failed. Please check your internet connection.');
      }

      throw new NetworkError(`Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get the access token for data API requests (returns JWT token)
   */
  private async getAccessToken(): Promise<string | null> {
    return this.getJWTToken();
  }

  /**
   * Build request headers with authentication and content type
   */
  private async buildHeaders(
    customHeaders: Record<string, string> = {},
    requireAuth: boolean = true,
    _retryCount: number = 0
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    if (requireAuth) {
      const token = await this.getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else if (_retryCount < 2) {
        // Retry after short delay -- handles auth session initialization race
        logger.log(`Token not available, retrying (${_retryCount + 1}/2)...`);
        await new Promise(resolve => setTimeout(resolve, 500 * (_retryCount + 1)));
        return this.buildHeaders(customHeaders, requireAuth, _retryCount + 1);
      } else {
        throw new ApiError(401, 'Unauthorized', 'No valid access token available');
      }
    }

    return headers;
  }

  /**
   * Execute HTTP request with error handling and retries
   */
  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const {
      method = 'GET',
      body,
      headers: customHeaders = {},
      requireAuth = true,
      signal,
      timeout = 30000, // Default 30 second timeout
    } = config;

    const url = `${this.baseUrl}${endpoint}`;

    logger.log(`${method} ${url}`);

    // Create abort controller for timeout if signal not provided
    const abortController = signal ? null : new AbortController();
    const requestSignal = signal || abortController!.signal;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Set up timeout
    if (!signal && abortController) {
      timeoutId = setTimeout(() => {
        abortController.abort();
      }, timeout);
    }

    try {
      const headers = await this.buildHeaders(customHeaders, requireAuth);

      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: requestSignal,
      });

      // Clear timeout on successful response
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      logger.log(`${method} ${endpoint} - ${response.status} ${response.statusText}`);

      // Handle non-JSON responses (e.g., 204 No Content)
      if (response.status === 204) {
        logger.log('204 No Content - returning empty object');
        return {} as T;
      }

      let responseData: any;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        // Only log response data in dev mode, and only for errors
        if (!response.ok) {
          logger.error('Response data:', responseData);
        }
      } else {
        responseData = await response.text();
        // Only log response data in dev mode, and only for errors
        if (!response.ok) {
          logger.error('Response data (text):', responseData);
        }
      }

      if (!response.ok) {
        if (response.status === 401 && requireAuth && !config._retry401) {
          logger.log('Token expired, refreshing and retrying...');
          this.clearTokenCache();
          return this.request<T>(endpoint, { ...config, _retry401: true });
        }

        logger.error('Request failed:', {
          status: response.status,
          statusText: response.statusText,
          endpoint,
        });
        // Prefer nested error.message from gateway error responses
        const errorMessage = responseData?.error?.message || responseData?.message || `Request failed with status ${response.status}`;
        const apiError = new ApiError(
          response.status,
          response.statusText,
          errorMessage,
          responseData
        );
        // Capture server errors (5xx) and unexpected client errors to Sentry
        if (response.status >= 500) {
          Sentry.captureException(apiError, {
            tags: { component: 'apiRequest', endpoint, method },
            extra: { httpStatus: response.status, responseData },
          });
        }
        // Surface demo-block 403s to the user via toast before propagating.
        // Only 403 responses can be demo blocks; no need to invoke the helper for others.
        if (response.status === 403) {
          handleDemoRestricted(apiError);
        }
        throw apiError;
      }

      return responseData;
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Handle abort/timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        if (!signal && abortController) {
          logger.error('Request timeout:', { endpoint, timeout });
          throw new NetworkError(`Request timeout after ${timeout}ms`);
        }
        logger.error('Request aborted:', { endpoint });
        throw new NetworkError('Request was aborted');
      }

      logger.error('Request error:', {
        endpoint,
        error: error instanceof Error ? error.message : error,
      });

      if (error instanceof ApiError) {
        throw error;
      }

      const networkError = error instanceof TypeError && error.message.includes('fetch')
        ? new NetworkError('Network request failed. Please check your internet connection.')
        : new NetworkError(`Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`);

      Sentry.captureException(networkError, {
        tags: { component: 'apiRequest', endpoint, method },
        extra: { originalError: error instanceof Error ? error.message : String(error) },
      });
      throw networkError;
    }
  }

  // HTTP Methods
  async get<T>(endpoint: string, requireAuth: boolean = true, signal?: AbortSignal, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', requireAuth, signal, timeout });
  }

  async post<T>(endpoint: string, body?: any, requireAuth: boolean = true, signal?: AbortSignal, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, requireAuth, signal, timeout });
  }

  async put<T>(endpoint: string, body?: any, requireAuth: boolean = true, signal?: AbortSignal, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, requireAuth, signal, timeout });
  }

  async patch<T>(endpoint: string, body?: any, requireAuth: boolean = true, signal?: AbortSignal, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, requireAuth, signal, timeout });
  }

  async delete<T>(endpoint: string, requireAuth: boolean = true, signal?: AbortSignal, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', requireAuth, signal, timeout });
  }

  /**
   * Upload a file using multipart/form-data
   */
  async uploadFile<T>(
    endpoint: string,
    file: { uri: string; type?: string; name?: string; file?: File },
    fieldName: string = 'file',
    additionalFields?: Record<string, string>,
    requireAuth: boolean = true,
    signal?: AbortSignal,
    timeout: number = 60000 // 60 second timeout for uploads
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    logger.log(`UPLOAD ${url}`);

    // Create abort controller for timeout if signal not provided
    const abortController = signal ? null : new AbortController();
    const requestSignal = signal || abortController!.signal;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Set up timeout
    if (!signal && abortController) {
      timeoutId = setTimeout(() => {
        abortController.abort();
      }, timeout);
    }

    try {
      // Build headers for multipart upload (don't set Content-Type, let fetch handle it)
      const headers: Record<string, string> = {};

      if (requireAuth) {
        const token = await this.getAccessToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else {
          throw new ApiError(401, 'Unauthorized', 'No valid access token available');
        }
      }

      // Delegate the actual multipart send to the platform-resolved transport:
      // native uses expo-file-system File.upload() (streams from the URI); web
      // uses fetch + FormData with a real browser File. See upload-transport.*.
      const transportResponse = await sendMultipart({
        url,
        file,
        fieldName,
        parameters: additionalFields,
        headers,
        signal: requestSignal,
      });

      // Clear timeout on successful response
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      logger.log(`Upload response status: ${transportResponse.status}`);

      let responseData: any;
      const { contentType, body, ok, status } = transportResponse;

      if (contentType && contentType.includes('application/json')) {
        responseData = body ? JSON.parse(body) : {};
        if (!ok) {
          logger.error('Upload response data:', responseData);
        }
      } else {
        responseData = body;
        if (!ok) {
          logger.error('Upload response data (text):', responseData);
        }
      }

      if (!ok) {
        logger.error('Upload failed:', {
          status,
          endpoint,
        });
        // Prefer nested error.message from gateway error responses
        const errorMessage = responseData?.error?.message || responseData?.message || `Upload failed with status ${status}`;
        const apiError = new ApiError(
          status,
          `${status}`,
          errorMessage,
          responseData
        );
        Sentry.captureException(apiError, {
          tags: { component: 'uploadFile', endpoint },
          extra: {
            httpStatus: status,
            fileType: file.type,
            fileName: file.name,
            uriPrefix: file.uri?.substring(0, 10),
            hasFileRef: !!file.file,
            responseData,
          },
        });
        // Surface demo-block 403s to the user via toast before propagating.
        if (status === 403) {
          handleDemoRestricted(apiError);
        }
        throw apiError;
      }

      logger.log('Upload successful');
      return responseData;
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Handle abort/timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        const abortError = !signal && abortController
          ? new NetworkError(`Upload timeout after ${timeout}ms`)
          : new NetworkError('Upload was aborted');
        logger.error(abortError.message, { endpoint, timeout });
        Sentry.captureException(abortError, {
          tags: { component: 'uploadFile', endpoint },
          extra: { fileType: file.type, fileName: file.name, hasFileRef: !!file.file },
        });
        throw abortError;
      }

      logger.error('Upload error:', {
        endpoint,
        error: error instanceof Error ? error.message : error,
      });

      if (error instanceof ApiError) {
        throw error;
      }

      const networkError = error instanceof TypeError && error.message.includes('fetch')
        ? new NetworkError('Network request failed. Please check your internet connection.')
        : new NetworkError(`Unexpected upload error: ${error instanceof Error ? error.message : 'Unknown error'}`);

      Sentry.captureException(networkError, {
        tags: { component: 'uploadFile', endpoint },
        extra: {
          originalError: error instanceof Error ? error.message : String(error),
          fileType: file.type,
          fileName: file.name,
          uriPrefix: file.uri?.substring(0, 10),
          hasFileRef: !!file.file,
        },
      });
      throw networkError;
    }
  }

  /**
   * Get the current JWT token (for WebSocket auth or other direct use).
   */
  async getToken(): Promise<string | null> {
    return this.getJWTToken();
  }

  // Utility Methods
  getBaseUrl(): string {
    return this.baseUrl;
  }

  updateBaseUrl(newBaseUrl: string): void {
    this.baseUrl = newBaseUrl;
  }

  /**
   * Clear cached JWT token (useful for debugging or logout)
   */
  clearTokenCache(): void {
    logger.log('Clearing JWT token cache');
    this.jwtToken = null;
    this.jwtExpiresAt = null;
    this.jwtRefresh = null;
  }

  /**
   * Get token cache status (for debugging)
   */
  getTokenCacheStatus(): { hasToken: boolean; expiresAt: string | null; isExpired: boolean } {
    const now = new Date();
    return {
      hasToken: !!this.jwtToken,
      expiresAt: this.jwtExpiresAt?.toISOString() || null,
      isExpired: this.jwtExpiresAt ? now >= this.jwtExpiresAt : false
    };
  }

  /**
   * Test JWT token retrieval (for debugging)
   */
  async testJWTRetrieval(): Promise<{ success: boolean; method: string | null; tokenLength: number | null; error?: string }> {
    try {
      this.clearTokenCache(); // Force fresh retrieval
      const token = await this.getJWTToken();

      if (token) {
        const method = token === this.jwtToken ? 'endpoint' : 'header';
        return {
          success: true,
          method,
          tokenLength: token.length
        };
      } else {
        return {
          success: false,
          method: null,
          tokenLength: null,
          error: 'No token retrieved'
        };
      }
    } catch (error) {
      return {
        success: false,
        method: null,
        tokenLength: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export individual methods for convenience
export const { get, post, put, patch, delete: del } = apiClient;
